import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { X, UserPlus, Loader2 } from 'lucide-react';
import { addFamilyMember, sendFamilyOtp, sendFamilyPhoneOtp } from '../../redux/familySlice';
import GenderSelect from '../common/GenderSelect';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';

// Add someone to a family account.
//
// A member has NO email and NO password — they are reached by switching profile
// from the primary's login. So this form deliberately does not ask for them:
// the backend builds the record without those keys at all, which is also why
// the "email is required" rule doesn't reject a member for lacking one.
//
// `primaryPatientId` is passed only by staff (doctor / receptionist adding a
// member for a patient at the desk). A patient always adds to their own family
// whatever the client sends.
export default function AddFamilyMember({ isOpen, onClose, primaryPatientId = null, onAdded }) {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { relationships, consentRequiredFor, saving } = useSelector((s) => s.family || {});


    // Email and phone are OPTIONAL. Left blank, the member is reached through
    // the contact person. Filled in, the email must be verified — and verifying
    // it is what earns them their own login, so it cannot be skipped.
    const EMPTY = {
        name: '', surname: '', gender: '', dob: '', relationship: '',
        consentConfirmed: false, email: '', phone: '', emailOtp: '', phoneOtp: '',
    };
    const [otpSent, setOtpSent] = useState(false);
    const [sendingOtp, setSendingOtp] = useState(false);
    const [phoneOtpSent, setPhoneOtpSent] = useState(false);
    const [sendingPhoneOtp, setSendingPhoneOtp] = useState(false);
    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (!isOpen) return;
        setForm(EMPTY); setErrors({}); setOtpSent(false); setPhoneOtpSent(false);
    }, [isOpen, dispatch]);



    const requestOtp = async () => {
        const email = form.email.trim();
        if (!email) { setErrors((e) => ({ ...e, email: t('family.email_required_first', 'Enter an email address first') })); return; }
        setSendingOtp(true);
        try {
            await dispatch(sendFamilyOtp(email)).unwrap();
            setOtpSent(true);
            toast.info(t('family.otp_sent', 'We sent a verification code to that address'));
        } catch {
            toast.error(t('family.otp_send_failed', 'Could not send the code'));
        } finally {
            setSendingOtp(false);
        }
    };

    const requestPhoneOtp = async () => {
        const phone = form.phone.trim();
        if (!phone) { setErrors((e) => ({ ...e, phone: t('family.phone_required_first', 'Enter a mobile number first') })); return; }
        setSendingPhoneOtp(true);
        try {
            await dispatch(sendFamilyPhoneOtp(phone)).unwrap();
            setPhoneOtpSent(true);
            toast.info(t('family.otp_sent_sms', 'We sent a verification code to that number'));
        } catch {
            toast.error(t('family.otp_send_failed', 'Could not send the code'));
        } finally {
            setSendingPhoneOtp(false);
        }
    };

    if (!isOpen) return null;

    // Same casing rule as every other person form: first name and surname both
    // get an initial capital. The server applies it too, so this is immediate
    // feedback rather than the only place it happens.
    const set = (k, v) => {
        let val = v;
        if (k === 'name') val = formatFirstName(val);
        if (k === 'surname') val = formatSurname(val);
        setForm((f) => ({ ...f, [k]: val }));
        setErrors((e) => ({ ...e, [k]: undefined }));
    };

    // Adult relationships ask the primary to confirm they have permission. Not
    // an age rule — evidence of consent, because one adult is about to be able
    // to read another adult's medical record.
    const needsConsent = (consentRequiredFor || []).includes(form.relationship);

    const validate = () => {
        const e = {};
        if (!form.name.trim()) e.name = t('family.name_required', 'First name is required');
        if (!form.surname.trim()) e.surname = t('family.surname_required', 'Surname is required');
        if (!form.gender) e.gender = t('family.gender_required', 'Gender is required');
        if (!form.relationship) e.relationship = t('family.relationship_required', 'Please choose the relationship');
        if (needsConsent && !form.consentConfirmed) {
            e.consentConfirmed = t('family.consent_required', 'Please confirm you have their permission');
        }
        // Only when the field was actually filled — blank is a valid choice for
        // both. But anything typed in has to be proven: an unverified address or
        // number on the record is worse than none, because later flows read it
        // as if somebody had confirmed it.
        if (form.email.trim() && !form.emailOtp.trim()) {
            e.emailOtp = t('family.otp_required', 'Enter the code sent to that email');
        }
        if (form.phone.trim() && !form.phoneOtp.trim()) {
            e.phoneOtp = t('family.phone_otp_required', 'Enter the code sent to that number');
        }
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const submit = async () => {
        if (!validate()) return;
        try {
            const res = await dispatch(addFamilyMember({
                ...form,
                // Send the keys only when filled: an empty email would be
                // rejected by the "email is required" rule, which is exactly
                // what must NOT happen to an optional field.
                email: form.email.trim() || undefined,
                phone: form.phone.trim() || undefined,
                emailOtp: form.emailOtp.trim() || undefined,
                phoneOtp: form.phoneOtp.trim() || undefined,
                ...(primaryPatientId ? { primaryPatientId } : {}),
            })).unwrap();
            toast.success(t('family.added', 'Family member added'));
            if (typeof onAdded === 'function') onAdded(res?.member);
            onClose();
        } catch (err) {
            const msg = err?.en || err?.message?.en || (typeof err === 'string' ? err : null);
            toast.error(msg || t('family.add_failed', 'Could not add the family member'));
        }
    };

    const field = 'w-full p-2 border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#4ABDBD]';

    // RENDERED THROUGH A PORTAL, deliberately.
    //
    // This modal is opened from inside the sidebar, and the sidebar carries a
    // CSS `transform` for its slide animation. A transform makes an element the
    // containing block for any position:fixed descendant — so without a portal
    // this "full screen" overlay was trapped inside the sidebar's narrow column
    // and rendered squeezed and clipped against the page.
    //
    // document.body sits outside that transform, so the overlay covers the page.
    return createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
            <div data-testid="add-family-member-modal" className="w-full max-w-md rounded-2xl bg-white shadow-xl">
                <div className="flex items-center justify-between border-b border-gray-100 px-5 py-4">
                    <h3 className="flex items-center gap-2 font-bold text-[#1a365d]">
                        <UserPlus className="w-4 h-4 text-[#4ABDBD]" />
                        {t('family.add_secondary', 'Add secondary patient')}
                    </h3>
                    <button type="button" onClick={onClose} className="text-gray-400 hover:text-red-500 cursor-pointer">
                        <X className="w-5 h-5" />
                    </button>
                </div>

                <div className="px-5 py-4 flex flex-col gap-3">
                    <p className="rounded-xl bg-gray-50 px-3 py-2 text-[11px] leading-relaxed text-gray-600">
                        {t('family.add_hint',
                            'This person shares your login. They do not need their own email or password — you switch between profiles. You can give them their own account at any time.')}
                    </p>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium text-slate-500">{t('family.first_name', 'First name')}</label>
                            <input data-testid="family-name" value={form.name} onChange={(e) => set('name', e.target.value)}
                                className={`${field} ${errors.name ? 'border-red-400' : 'border-gray-300'}`} />
                            {errors.name && <p className="mt-1 text-[11px] text-red-600">{errors.name}</p>}
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500">{t('family.surname', 'Surname')}</label>
                            <input data-testid="family-surname" value={form.surname} onChange={(e) => set('surname', e.target.value)}
                                className={`${field} ${errors.surname ? 'border-red-400' : 'border-gray-300'}`} />
                            {errors.surname && <p className="mt-1 text-[11px] text-red-600">{errors.surname}</p>}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-medium text-slate-500">{t('family.gender', 'Gender')}</label>
                            <GenderSelect
                                data-testid="family-gender"
                                value={form.gender}
                                onChange={(v) => set('gender', v)}
                                options={getGenderOptions(t)}
                                placeholder={t('family.select', 'Select')}
                                error={!!errors.gender}
                            />
                            {errors.gender && <p className="mt-1 text-[11px] text-red-600">{errors.gender}</p>}
                        </div>
                        <div>
                            <label className="text-xs font-medium text-slate-500">{t('family.dob', 'Date of birth')}</label>
                            <input data-testid="family-dob" type="date" value={form.dob}
                                max={new Date().toISOString().split('T')[0]}
                                onChange={(e) => set('dob', e.target.value)}
                                className={`${field} border-gray-300`} />
                        </div>
                    </div>

                    <div>
                        <label className="text-xs font-medium text-slate-500">{t('family.relationship', 'Relationship')}</label>
                        <select data-testid="family-relationship" value={form.relationship}
                            onChange={(e) => set('relationship', e.target.value)}
                            className={`tt-select ${field} bg-white ${errors.relationship ? 'border-red-400' : 'border-gray-300'}`}>
                            <option value="">{t('family.select', 'Select')}</option>
                            {(relationships || []).map((r) => (
                                <option key={r} value={r}>{t(`family.rel.${r.toLowerCase()}`, r)}</option>
                            ))}
                        </select>
                        {errors.relationship && <p className="mt-1 text-[11px] text-red-600">{errors.relationship}</p>}
                    </div>

                    {/* OPTIONAL contact details. Given an email, the member gets
                        their own login once it is verified. */}
                    <div className="rounded-xl border border-dashed border-gray-300 px-3 py-3">
                        <p className="mb-2 text-[11px] font-semibold text-gray-600">
                            {t('family.contact_optional', 'Contact details (optional)')}
                        </p>
                        <p className="mb-2 text-[11px] leading-relaxed text-gray-500">
                            {t('family.contact_hint_v2',
                                'Leave both blank and they are reached through you. Anything you fill in has to be verified. A verified email also gives them their own sign-in.')}
                        </p>

                        <div className="flex gap-2">
                            <input
                                data-testid="family-email"
                                value={form.email}
                                onChange={(e) => set('email', e.target.value)}
                                placeholder={t('family.email', 'Email')}
                                className={`${field} flex-1 ${errors.email ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            <button
                                type="button"
                                data-testid="family-send-otp"
                                onClick={requestOtp}
                                disabled={sendingOtp || !form.email.trim()}
                                className="whitespace-nowrap rounded-xl border border-[#4ABDBD] px-3 text-xs font-semibold text-[#3AA8A8] transition hover:bg-[#4ABDBD]/5 disabled:opacity-50 cursor-pointer"
                            >
                                {sendingOtp
                                    ? t('family.sending', 'Sending…')
                                    : (otpSent ? t('family.resend', 'Resend') : t('family.verify', 'Verify'))}
                            </button>
                        </div>
                        {errors.email && <p className="mt-1 text-[11px] text-red-600">{errors.email}</p>}

                        {otpSent && (
                            <input
                                data-testid="family-email-otp"
                                value={form.emailOtp}
                                onChange={(e) => set('emailOtp', e.target.value)}
                                placeholder={t('family.enter_code', 'Enter the 6-digit code')}
                                className={`${field} mt-2 ${errors.emailOtp ? 'border-red-400' : 'border-gray-300'}`}
                            />
                        )}
                        {errors.emailOtp && <p className="mt-1 text-[11px] text-red-600">{errors.emailOtp}</p>}

                        <div className="mt-2 flex gap-2">
                            <input
                                data-testid="family-phone"
                                value={form.phone}
                                disabled={phoneOtpSent}
                                onChange={(e) => set('phone', e.target.value.replace(/[^\d+\-\s]/g, ''))}
                                placeholder={t('family.phone', 'Mobile number')}
                                className={`${field} flex-1 ${errors.phone ? 'border-red-400' : 'border-gray-300'}`}
                            />
                            <button
                                type="button"
                                data-testid="family-send-phone-otp"
                                onClick={requestPhoneOtp}
                                disabled={sendingPhoneOtp || !form.phone.trim()}
                                className="whitespace-nowrap rounded-xl border border-[#4ABDBD] px-3 text-xs font-semibold text-[#3AA8A8] transition hover:bg-[#4ABDBD]/5 disabled:opacity-50 cursor-pointer"
                            >
                                {sendingPhoneOtp
                                    ? t('family.sending', 'Sending…')
                                    : (phoneOtpSent ? t('family.resend', 'Resend') : t('family.verify', 'Verify'))}
                            </button>
                        </div>
                        {errors.phone && <p className="mt-1 text-[11px] text-red-600">{errors.phone}</p>}

                        {phoneOtpSent && (
                            <input
                                data-testid="family-phone-otp"
                                value={form.phoneOtp}
                                maxLength={6}
                                inputMode="numeric"
                                onChange={(e) => set('phoneOtp', e.target.value.replace(/\D/g, ''))}
                                placeholder={t('family.enter_code', 'Enter the 6-digit code')}
                                className={`${field} mt-2 ${errors.phoneOtp ? 'border-red-400' : 'border-gray-300'}`}
                            />
                        )}
                        {errors.phoneOtp && <p className="mt-1 text-[11px] text-red-600">{errors.phoneOtp}</p>}
                    </div>

                    {needsConsent && (
                        <label className="flex items-start gap-2.5 rounded-xl bg-amber-50 border border-amber-200 px-3 py-2.5 cursor-pointer">
                            <input
                                type="checkbox"
                                data-testid="family-consent"
                                checked={form.consentConfirmed}
                                onChange={(e) => set('consentConfirmed', e.target.checked)}
                                className="mt-0.5 accent-[#4ABDBD] cursor-pointer"
                            />
                            <span className="text-[11px] leading-relaxed text-amber-900">
                                {t('family.consent_text',
                                    'I confirm I have this person’s permission to manage their health records on their behalf.')}
                            </span>
                        </label>
                    )}
                    {errors.consentConfirmed && <p className="text-[11px] text-red-600">{errors.consentConfirmed}</p>}
                </div>

                <div className="flex justify-end gap-2 border-t border-gray-100 px-5 py-4">
                    <button type="button" onClick={onClose}
                        className="rounded-xl border border-gray-300 px-4 py-2 text-sm text-gray-600 hover:bg-gray-50 cursor-pointer">
                        {t('family.cancel', 'Cancel')}
                    </button>
                    <button type="button" data-testid="family-submit" onClick={submit} disabled={saving}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] px-5 py-2 text-sm font-semibold text-white transition hover:shadow-md disabled:opacity-60 cursor-pointer">
                        {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                        {t('family.add', 'Add')}
                    </button>
                </div>
            </div>
        </div>
        ,
        document.body
    );
}
